/**
 * useComponentListQuery
 * Fetches tenant component list via React Query and syncs to localStorage on success.
 * Use this hook wherever the component list API is needed so all callers share the same cache.
 */

import { useQuery } from "react-query";
import {
  fetchComponentsFromAPI,
  saveComponentList,
  getComponentList,
  COMPONENT_LIST_QUERY_KEY,
} from "@/common/utils/componentListService";

export interface UseComponentListQueryOptions {
  /** When false, query does not run automatically; use refetch() to load. Default true when not set. */
  enabled?: boolean;
}

/**
 * useQuery for tenant/admin components. On success, saves to localStorage for use by
 * getComponentList() and the rest of the app. Shared query key so all callers use the same cache.
 */
export function useComponentListQuery(options?: UseComponentListQueryOptions) {
  return useQuery<string[] | null>(
    COMPONENT_LIST_QUERY_KEY,
    fetchComponentsFromAPI,
    {
      enabled: options?.enabled ?? true,
      // Pre-populate from localStorage so the query starts with cached data.
      // Combined with staleTime: Infinity this prevents any API call on page
      // reload when the list is already stored — the API is only hit when
      // localStorage is empty (e.g. first login or after logout).
      initialData: () => getComponentList() ?? undefined,
      // Component list is session-scoped — only changes on login/logout.
      // Prevent unnecessary refetches on window focus or network reconnect.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          saveComponentList(data);
        }
      },
    }
  );
}
